import React, { useCallback, useEffect, useMemo, useState } from 'react'
import './ModalTreatedSymptoms.css'

const normalizeItems = (items = []) => (
    (Array.isArray(items) ? items : []).map((item) => ({
        id: Number(item?.treated_symptom_id ?? item?.id ?? 0),
        symptom: String(item?.symptom ?? item?.description ?? '').trim()
    }))
);

const sortItems = (items = []) => (
    [...items].sort((left, right) => String(left.symptom ?? '').localeCompare(String(right.symptom ?? ''), undefined, { sensitivity: 'base' }))
);

const ModalTreatedSymptoms = ({ closeModal, initialTreatedSymptoms = [], isLoading = false, onRefreshTreatedSymptoms }) => {
    const [treatedSymptoms, setTreatedSymptoms] = useState(sortItems(normalizeItems(initialTreatedSymptoms)));
    const [baselineTreatedSymptoms, setBaselineTreatedSymptoms] = useState(sortItems(normalizeItems(initialTreatedSymptoms)));
    const [newSymptom, setNewSymptom] = useState('');
    const [searchText, setSearchText] = useState('');
    const [isModalLoading, setIsModalLoading] = useState(true);
    const [isAdding, setIsAdding] = useState(false);
    const [isSavingChanges, setIsSavingChanges] = useState(false);
    const [savingIds, setSavingIds] = useState([]);
    const [submitError, setSubmitError] = useState('');

    const headers = useMemo(() => ({
        'machine':'2',
        'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
        'content-type': 'application/json'
    }), []);

    const readErrorMessage = async (response) => {
        try {
            const text = await response.text();
            if (!text.trim()) {
                return `Fetch error - ${response.url} - Status: ${response.status}`;
            }

            try {
                const payload = JSON.parse(text);
                if (typeof payload === 'string' && payload.trim()) {
                    return payload.trim();
                }

                if (payload && typeof payload === 'object') {
                    const flattenedMessage = Object.entries(payload)
                        .flatMap(([key, value]) => Array.isArray(value) ? value.map((item) => `${key}: ${item}`) : `${key}: ${value}`)
                        .filter(Boolean)
                        .join(' ');

                    if (flattenedMessage) {
                        return flattenedMessage;
                    }
                }
            } catch {
                return text.trim();
            }
        } catch {
            return `Fetch error - ${response.url} - Status: ${response.status}`;
        }

        return `Fetch error - ${response.url} - Status: ${response.status}`;
    };

    const refreshModalData = useCallback(async () => {
        setIsModalLoading(true);
        setSubmitError('');

        try {
            const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/treated_symptoms/', {
                method: 'GET',
                headers: {
                    'machine':'2',
                    'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                },
            });

            if (!response.ok) {
                throw new Error(await readErrorMessage(response));
            }

            const payload = await response.json();
            const normalizedItems = sortItems(normalizeItems(payload));
            setTreatedSymptoms(normalizedItems);
            setBaselineTreatedSymptoms(normalizedItems);
        } catch (error) {
            console.error(error);
            setSubmitError(error.message || 'Unable to load treated symptoms.');
        } finally {
            setIsModalLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isLoading && initialTreatedSymptoms.length > 0) {
            const normalizedItems = sortItems(normalizeItems(initialTreatedSymptoms));
            setTreatedSymptoms(normalizedItems);
            setBaselineTreatedSymptoms(normalizedItems);
            setIsModalLoading(false);
        }

        refreshModalData();
    }, [initialTreatedSymptoms, isLoading, refreshModalData]);

    const baselineMap = useMemo(() => new Map(
        baselineTreatedSymptoms.map((item) => [String(item.id), item.symptom])
    ), [baselineTreatedSymptoms]);

    const normalizedSearchText = searchText.trim().toLowerCase();

    const filteredTreatedSymptoms = useMemo(() => (
        treatedSymptoms
            .filter((item) => (
                !normalizedSearchText
                || String(item.symptom ?? '').toLowerCase().includes(normalizedSearchText)
            ))
    ), [normalizedSearchText, treatedSymptoms]);

    const dirtyTreatedSymptoms = useMemo(() => (
        treatedSymptoms.filter((item) => String(item.symptom ?? '').trim() !== String(baselineMap.get(String(item.id)) ?? '').trim())
    ), [baselineMap, treatedSymptoms]);

    const hasPendingChanges = dirtyTreatedSymptoms.length > 0;

    const setSavingForId = (treatedSymptomId, isSavingValue) => {
        setSavingIds((currentSavingIds) => (
            isSavingValue
                ? [...new Set([...currentSavingIds, treatedSymptomId])]
                : currentSavingIds.filter((id) => id !== treatedSymptomId)
        ));
    };

    const handleAddSymptom = async () => {
        const trimmedSymptom = newSymptom.trim();
        if (!trimmedSymptom) {
            return;
        }

        setIsAdding(true);
        setSubmitError('');

        try {
            const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/treated_symptoms/', {
                method: 'POST',
                headers,
                body: JSON.stringify({ symptom: trimmedSymptom }),
            });

            if (!response.ok) {
                throw new Error(await readErrorMessage(response));
            }

            setNewSymptom('');
            await refreshModalData();
            if (onRefreshTreatedSymptoms) {
                await onRefreshTreatedSymptoms();
            }
        } catch (error) {
            console.error(error);
            setSubmitError(error.message || 'Unable to add treated symptom.');
        } finally {
            setIsAdding(false);
        }
    };

    const handleRowChange = (treatedSymptomId, value) => {
        setSubmitError('');
        setTreatedSymptoms((currentItems) => currentItems.map((item) => (
            item.id === treatedSymptomId
                ? { ...item, symptom: value }
                : item
        )));
    };

    const handleSaveChanges = async () => {
        const validDirtyTreatedSymptoms = dirtyTreatedSymptoms.filter((item) => item.id && String(item.symptom ?? '').trim());
        if (!validDirtyTreatedSymptoms.length) {
            return;
        }

        setIsSavingChanges(true);
        setSubmitError('');

        try {
            for (const treatedSymptom of validDirtyTreatedSymptoms) {
                setSavingForId(treatedSymptom.id, true);
                const response = await fetch(process.env.REACT_APP_API_URL_BASE + '/treated_symptoms/' + treatedSymptom.id + '/', {
                    method: 'PATCH',
                    headers,
                    body: JSON.stringify({ symptom: String(treatedSymptom.symptom ?? '').trim() }),
                });

                if (!response.ok) {
                    throw new Error(await readErrorMessage(response));
                }

                setSavingForId(treatedSymptom.id, false);
            }

            await refreshModalData();
            if (onRefreshTreatedSymptoms) {
                await onRefreshTreatedSymptoms();
            }
        } catch (error) {
            console.error(error);
            setSubmitError(error.message || 'Unable to update treated symptoms.');
        } finally {
            setIsSavingChanges(false);
            setSavingIds([]);
        }
    };

    return (
        <div className='modal-container'>
            <div className='modal-edit treated-symptoms-modal'>
                <button className='modal-close-btn' onClick={closeModal} aria-label='Close treated symptoms modal'>X</button>
                <div className='treated-symptoms-page-controls'>
                    <div className='treated-symptoms-page-header'>
                        <h3>Add/Edit Treated Symptoms</h3>
                        <div className='treated-symptoms-count'>
                            {filteredTreatedSymptoms.length} of {treatedSymptoms.length}
                        </div>
                    </div>

                    <div className='treated-symptoms-add-row'>
                        <div className='form-group wide'>
                            <label htmlFor='new-treated-symptom'>New Treated Symptom</label>
                            <input
                                id='new-treated-symptom'
                                value={newSymptom}
                                onChange={(event) => setNewSymptom(event.target.value)}
                                placeholder='Enter a treated symptom'
                            />
                        </div>
                        <button
                            type='button'
                            className='btn treated-symptoms-save-btn'
                            onClick={handleAddSymptom}
                            disabled={isAdding || !newSymptom.trim()}
                        >
                            {isAdding ? 'Adding...' : 'Add'}
                        </button>
                    </div>

                    <div className='treated-symptoms-toolbar'>
                        <div className='form-group wide'>
                            <label htmlFor='treated-symptoms-search'>Search Treated Symptoms</label>
                            <input
                                id='treated-symptoms-search'
                                value={searchText}
                                onChange={(event) => setSearchText(event.target.value)}
                                placeholder='Search treated symptoms'
                            />
                        </div>
                    </div>
                </div>

                {submitError && <div className='error'>{submitError}</div>}

                <div className='client-card-grid'>
                    <section className='client-card treated-symptoms-card'>
                        <div className='client-card-header'>
                            <h3>Add/Edit Treated Symptoms</h3>
                        </div>

                        {isModalLoading ? (
                            <div className='client-skeleton-grid treated-symptoms-modal-skeleton'>
                                <div className='skeleton-line treated-symptoms-skeleton-header' />
                                {Array.from({ length: 6 }).map((_, index) => (
                                    <div key={`treated-symptoms-skeleton-${index}`} className='treated-symptoms-table-skeleton-row'>
                                        <div className='skeleton-line treated-symptoms-skeleton-line' />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className='treated-symptoms-table-wrap'>
                                <table className='treated-symptoms-table'>
                                    <thead>
                                        <tr>
                                            <th className='left'>Symptom</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredTreatedSymptoms.map((treatedSymptom) => {
                                            const isSavingCurrent = savingIds.includes(treatedSymptom.id);
                                            const trimmedSymptom = String(treatedSymptom.symptom ?? '').trim();
                                            const isChanged = trimmedSymptom !== String(baselineMap.get(String(treatedSymptom.id)) ?? '').trim();

                                            return (
                                                <tr key={treatedSymptom.id} className={isChanged ? 'edited' : ''}>
                                                    <td className='left treated-symptoms-edit-cell'>
                                                        <input
                                                            className='treated-symptoms-cell-input'
                                                            value={treatedSymptom.symptom}
                                                            onChange={(event) => handleRowChange(treatedSymptom.id, event.target.value)}
                                                            aria-label={`Treated symptom ${treatedSymptom.id}`}
                                                        />
                                                        {isSavingCurrent && <span className='treated-symptoms-row-status'>Saving...</span>}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                {!filteredTreatedSymptoms.length && (
                                    <div className='treated-symptoms-empty-state'>No treated symptoms found.</div>
                                )}
                            </div>
                        )}

                        <div className='client-actions-row'>
                            {hasPendingChanges && (
                                <button
                                    type='button'
                                    className='btn treated-symptoms-save-btn'
                                    onClick={handleSaveChanges}
                                    disabled={isSavingChanges || dirtyTreatedSymptoms.some((item) => !String(item.symptom ?? '').trim())}
                                >
                                    {isSavingChanges ? 'Saving...' : `Save Changes (${dirtyTreatedSymptoms.length})`}
                                </button>
                            )}
                        </div>
                    </section>
                </div>

                <div className='treated-symptoms-page-actions'>
                    <button type='button' className='btn btn-cancel treated-symptoms-close-btn' onClick={closeModal}>Close</button>
                </div>
            </div>
        </div>
    )
}

export default ModalTreatedSymptoms